import { useCallback, useEffect, useRef, useState } from 'react';
import {
  STICKY_NOTE_MAX_HEIGHT,
  STICKY_NOTE_MAX_WIDTH,
  STICKY_NOTE_MIN_HEIGHT,
  STICKY_NOTE_MIN_WIDTH,
  STICKY_NOTE_WIDTH,
  type StickyNote as StickyNoteModel,
} from './types';

const NOTE_BASE_Z = 1040;

type StickyNoteProps = {
  note: StickyNoteModel;
  onUpdate: (
    id: string,
    patch: Partial<Pick<StickyNoteModel, 'text' | 'x' | 'y' | 'width' | 'height' | 'zIndex' | 'minimized'>>,
  ) => void;
  onBringToFront: (id: string) => void;
  onRemove: (id: string) => void;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export default function StickyNote({ note, onUpdate, onBringToFront, onRemove }: StickyNoteProps) {
  const [text, setText] = useState(note.text);
  const [position, setPosition] = useState({ x: note.x, y: note.y });
  const [size, setSize] = useState({
    width: note.width || STICKY_NOTE_WIDTH,
    height: note.height || STICKY_NOTE_MIN_HEIGHT,
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const resizeRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originW: number;
    originH: number;
  } | null>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);

  useEffect(() => {
    setText(note.text);
  }, [note.id, note.text]);

  useEffect(() => {
    if (!isDraggingRef.current) {
      setPosition({ x: note.x, y: note.y });
    }
  }, [note.x, note.y]);

  useEffect(() => {
    if (!isResizingRef.current) {
      setSize({
        width: note.width || STICKY_NOTE_WIDTH,
        height: note.height || STICKY_NOTE_MIN_HEIGHT,
      });
    }
  }, [note.width, note.height]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const persistText = useCallback(
    (value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onUpdate(note.id, { text: value });
      }, 300);
    },
    [note.id, onUpdate],
  );

  const handleTextChange = (value: string) => {
    setText(value);
    persistText(value);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    onBringToFront(note.id);
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    isDraggingRef.current = true;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const maxX = Math.max(0, window.innerWidth - size.width);
    const maxY = Math.max(0, window.innerHeight - size.height);

    setPosition({
      x: clamp(drag.originX + dx, 0, maxX),
      y: clamp(drag.originY + dy, 0, maxY),
    });
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const maxX = Math.max(0, window.innerWidth - size.width);
    const maxY = Math.max(0, window.innerHeight - size.height);
    const next = {
      x: clamp(drag.originX + dx, 0, maxX),
      y: clamp(drag.originY + dy, 0, maxY),
    };

    dragRef.current = null;
    isDraggingRef.current = false;
    setPosition(next);
    onUpdate(note.id, next);

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }
  };

  const handleResizeDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onBringToFront(note.id);
    event.currentTarget.setPointerCapture(event.pointerId);
    isResizingRef.current = true;
    resizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originW: size.width,
      originH: size.height,
    };
  };

  const handleResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const resize = resizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;

    const maxW = Math.min(STICKY_NOTE_MAX_WIDTH, window.innerWidth - position.x);
    const maxH = Math.min(STICKY_NOTE_MAX_HEIGHT, window.innerHeight - position.y);
    setSize({
      width: clamp(resize.originW + (event.clientX - resize.startX), STICKY_NOTE_MIN_WIDTH, maxW),
      height: clamp(resize.originH + (event.clientY - resize.startY), STICKY_NOTE_MIN_HEIGHT, maxH),
    });
  };

  const handleResizeUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const resize = resizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;

    const maxW = Math.min(STICKY_NOTE_MAX_WIDTH, window.innerWidth - position.x);
    const maxH = Math.min(STICKY_NOTE_MAX_HEIGHT, window.innerHeight - position.y);
    const next = {
      width: clamp(resize.originW + (event.clientX - resize.startX), STICKY_NOTE_MIN_WIDTH, maxW),
      height: clamp(resize.originH + (event.clientY - resize.startY), STICKY_NOTE_MIN_HEIGHT, maxH),
    };

    resizeRef.current = null;
    isResizingRef.current = false;
    setSize(next);
    onUpdate(note.id, next);

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }
  };

  const handleMinimize = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    onUpdate(note.id, { text, minimized: true });
  };

  return (
    <div
      ref={noteRef}
      className={`sticky-note sticky-note--${note.color}`}
      style={{
        left: position.x,
        top: position.y,
        zIndex: NOTE_BASE_Z + note.zIndex,
        width: size.width,
        height: size.height,
      }}
      onFocusCapture={() => onBringToFront(note.id)}
      role="note"
      aria-label="Sticky note"
    >
      <div
        className="sticky-note__header"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <span className="sticky-note__drag-hint" aria-hidden>
          ⋮⋮
        </span>
        <div className="sticky-note__actions">
          <button
            type="button"
            className="sticky-note__action"
            onClick={handleMinimize}
            aria-label="Minimize sticky note"
            title="Minimize"
          >
            −
          </button>
          <button
            type="button"
            className="sticky-note__action sticky-note__action--delete"
            onClick={() => onRemove(note.id)}
            aria-label="Delete sticky note"
            title="Delete"
          >
            ×
          </button>
        </div>
      </div>
      <textarea
        className="sticky-note__textarea"
        value={text}
        onChange={e => handleTextChange(e.target.value)}
        placeholder="Write a note…"
        aria-label="Sticky note text"
      />
      <div
        className="sticky-note__resize"
        onPointerDown={handleResizeDown}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeUp}
        onPointerCancel={handleResizeUp}
        aria-hidden
        title="Resize"
      />
    </div>
  );
}
